import { store } from 'store';
import { ENVMAPPING } from 'utility/helper';
import { reauthenticateOnForbidden } from './handleAuthError';

const API_BASE = ENVMAPPING(window.location.origin).api;

export interface RequestOptions {
  method?: string;
  headers?: { [key: string]: string };
  body?: any;
}

export async function fetchService<T>(
  url: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', headers = {}, body = null } = options;

  const doFetch = (token: string | null): Promise<Response> => {
    const authHeaders: { [key: string]: string } = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    const combinedHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...headers,
      ...authHeaders,
    };
    return fetch(API_BASE + url, {
      method,
      headers: combinedHeaders,
      body: body ? JSON.stringify(body) : null,
      mode: 'cors',
    });
  };

  let response = await doFetch(store.getState().userData?.access_token ?? null);

  // API Gateway returns 403 when the Azure AD token is stale/expired. Try a
  // silent forced refresh and, if it succeeds, retry the request once.
  if (response.status === 403) {
    const refreshedToken = await reauthenticateOnForbidden();
    if (refreshedToken) {
      response = await doFetch(refreshedToken);
    }
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

/**
 * Resolve a Knowledge Base attachment (image) to a directly-loadable URL.
 *
 * The backend authorizes the request and returns a short-lived presigned S3
 * URL, which the browser loads directly from S3 as the <img> src. This avoids
 * proxying binary through the API Gateway (which corrupts raw image bodies).
 */
export async function fetchAttachmentObjectUrl(path: string): Promise<string> {
  let normalizedPath = (path || '').trim();

  if (normalizedPath.startsWith('/documentation/')) {
    normalizedPath = normalizedPath.replace(/^\/documentation\//, '');
  }
  if (normalizedPath.startsWith('/api/enc/')) {
    normalizedPath = normalizedPath.replace(/^\/api\/enc\//, '');
  }
  if (normalizedPath.startsWith('/')) {
    normalizedPath = normalizedPath.replace(/^\//, '');
  }

  const { url } = await fetchService<{ url: string }>(normalizedPath, { method: 'GET' });
  return url;
}
